import { CalendarDaysIcon, ClockIcon, CurrencyEuroIcon } from "@heroicons/react/24/solid";
import { FC, Fragment, HTMLAttributes, useEffect, useState, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import { z } from "zod";
import FormItem from "./FormItem";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Multiselect from "multiselect-react-dropdown";
import { Switch } from "@mui/material";
import { useLocation } from "react-router-dom";
import { ErrorMessage } from "../ErrorMessages";
import { FullDayRate, HalfDayRate, HourlyRate, LocationFormProps, SoireeRate } from "../interfaces";
import { calculateHourDifference, DAYS_OF_WEEK, formSchema, HOURS_OPTIONS } from "../utlis";

const LocationAvailibility: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const location = useLocation();
  const [applyToAll, setApplyToAll] = useState(false);
  const [priceErrorMsg, setPriceErrorMsg] = useState("");

  const [noPriceErrors, setNoPriceErrors] = useState<{ [key: string]: string }>({});
  const prevPricesRef = useRef<z.infer<typeof formSchema>["prices"]>([]);
  const [secondHalfDayEndHour, setSecondHalfDayEndHour] = useState<{ [key: string]: string | null }>({});
  const [hourlyRatesByDay, setHourlyRatesByDay] = useState<{
    [key: string]: {
      startHour: string;
      endHour: string;
      min: number;
      max: number;
      price: number;
      selectedDay: string;
      type: string;
    }[];
  }>({
    Lundi: [],
    Mardi: [],
    Mercredi: [],
    Jeudi: [],
    Vendredi: [],
    Samedi: [],
    Dimanche: [],
  });

  const [soireeRatesByDay, setSoireeRatesByDay] = useState<{
    [key: string]: {
      startHour: string;
      endHour: string;
      min: number;
      max: number;
      price: number;
      selectedDay: string;
      type: string;
    }[];
  }>({
    Lundi: [],
    Mardi: [],
    Mercredi: [],
    Jeudi: [],
    Vendredi: [],
    Samedi: [],
    Dimanche: [],
  });

  const [halfDayRatesByDay, setHalfDayRatesByDay] = useState<{
    [key: string]: {
      startHour: string;
      endHour: string;
      min: number;
      max: number;
      price: number;
      selectedDay: string;
      type: string;
    }[];
  }>({
    Lundi: [],
    Mardi: [],
    Mercredi: [],
    Jeudi: [],
    Vendredi: [],
    Samedi: [],
    Dimanche: [],
  });

  const [fullDayRatesByDay, setFullDayRatesByDay] = useState<{
    [key: string]: {
      startHour: string;
      endHour: string;
      min: number;
      max: number;
      price: number;
      selectedDay: string;
      type: string;
    }[];
  }>({
    Lundi: [],
    Mardi: [],
    Mercredi: [],
    Jeudi: [],
    Vendredi: [],
    Samedi: [],
    Dimanche: [],
  });

  const [enabledDays, setEnabledDays] = useState<Record<string, boolean>>(
    Object.fromEntries(DAYS_OF_WEEK.map((day) => [day.label, false]))
  );

  const [fixedHourlyHours, setFixedHourlyHours] = useState<{
    [key: string]: { startHour: string; endHour: string } | null;
  }>({
    Lundi: null,
    Mardi: null,
    Mercredi: null,
    Jeudi: null,
    Vendredi: null,
    Samedi: null,
    Dimanche: null,
  });

  const [fixedSoireeHours, setFixedSoireeHours] = useState<{
    [key: string]: { startHour: string; endHour: string } | null;
  }>({
    Lundi: null,
    Mardi: null,
    Mercredi: null,
    Jeudi: null,
    Vendredi: null,
    Samedi: null,
    Dimanche: null,
  });

  const [halfDayEndHour, setHalfDayEndHour] = useState<{
    [key: string]: string | null;
  }>({
    Lundi: null,
    Mardi: null,
    Mercredi: null,
    Jeudi: null,
    Vendredi: null,
    Samedi: null,
    Dimanche: null,
  });

  const [hourValidationErrors, setHourValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [soireeValidationErrors, setSoireeValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const [tempHourlyRatesByDay, setTempHourlyRatesByDay] = useState<{
    [key: string]: {
      startHour: string;
      endHour: string;
      min: number;
      max: number;
      price: number;
      selectedDay: string;
      type: "heure";
    };
  }>(() => {
    const initialRates: any = {};
    DAYS_OF_WEEK.forEach((day) => {
      initialRates[day.label] = {
        startHour: soireeRatesByDay[day.label]?.[0]?.startHour || "08:00",
        endHour: soireeRatesByDay[day.label]?.[0]?.startHour || "23:00",
        min: 1,
        max: 0,
        price: 0,
        selectedDay: day.label,
        type: "heure",
      };
    });
    return initialRates;
  });

  const [tempSoireeRatesByDay, setTempSoireeRatesByDay] = useState<{
    [key: string]: {
      startHour: string;
      endHour: string;
      min: number;
      max: number;
      price: number;
      selectedDay: string;
      type: "soirée";
    };
  }>(() => {
    const initialRates: any = {};
    DAYS_OF_WEEK.forEach((day) => {
      initialRates[day.label] = {
        startHour: hourlyRatesByDay[day.label]?.[0]?.endHour || "18:00",
        endHour: "23:00",
        min: 1,
        max: 0,
        price: 0,
        selectedDay: day.label,
        type: "soirée",
      };
    });
    return initialRates;
  });

  const [tempHalfDay, setTempHalfDay] = useState<{
    [key: string]: {
      startHour: string;
      endHour: string;
      min: number;
      max: number;
      price: number;
      selectedDay: string;
      type: "demi-journée";
    };
  }>(() => {
    const initialRates: any = {};
    DAYS_OF_WEEK.forEach((day) => {
      initialRates[day.label] = {
        startHour: "08:00",
        endHour: "23:00",
        min: 1,
        max: 0,
        price: 0,
        selectedDay: day.label,
        type: "demi-journée",
      };
    });
    return initialRates;
  });

  const [tempFullDay, setTempFullDay] = useState<{
    [key: string]: {
      startHour: string;
      endHour: string;
      min: number;
      max: number;
      price: number;
      selectedDay: string;
      type: "journée complète";
    };
  }>(() => {
    const initialRates: any = {};
    DAYS_OF_WEEK.forEach((day) => {
      initialRates[day.label] = {
        startHour: "08:00",
        endHour: "23:00",
        min: 1,
        max: 0,
        price: 0,
        selectedDay: day.label,
        type: "journée complète",
      };
    });
    return initialRates;
  });

  const getUpdatedPrices = () => {
    const prices = [
      ...Object.entries(hourlyRatesByDay)
        .filter(([day]) => enabledDays[day])
        .flatMap(([_, rates]) =>
          rates.map((rate) => ({
            price: rate.price,
            min: rate.min,
            max: rate.max,
            startHour: rate.startHour,
            endHour: rate.endHour,
            selectedDay: rate.selectedDay,
            type: "heure" as const,
          }))
        ),
      ...Object.entries(soireeRatesByDay)
        .filter(([day]) => enabledDays[day])
        .flatMap(([_, rates]) =>
          rates.map((rate) => ({
            price: rate.price,
            min: rate.min,
            max: rate.max,
            startHour: rate.startHour,
            endHour: rate.endHour,
            selectedDay: rate.selectedDay,
            type: "soirée" as const,
          }))
        ),
      ...Object.entries(halfDayRatesByDay)
        .filter(([day]) => enabledDays[day])
        .flatMap(([_, rates]) =>
          rates.map((rate) => ({
            price: rate.price,
            min: rate.min,
            max: rate.max,
            startHour: rate.startHour,
            endHour: rate.endHour,
            selectedDay: rate.selectedDay,
            type: "demi-journée" as const,
          }))
        ),
      ...Object.entries(fullDayRatesByDay)
        .filter(([day]) => enabledDays[day])
        .flatMap(([_, rates]) =>
          rates.map((rate) => ({
            price: rate.price,
            min: rate.min,
            max: rate.max,
            startHour: rate.startHour,
            endHour: rate.endHour,
            selectedDay: rate.selectedDay,
            type: "journée complète" as const,
          }))
        ),
    ];
    return prices;
  };

  const addHourlyRate = (day: string, rate: HourlyRate) => {
    // Remove the nbreHour validation for hourly rates
    setHourlyRatesByDay((prev) => ({
      ...prev,
      [day]: [...prev[day], { ...rate, selectedDay: day, type: "heure" }],
    }));
    setFixedHourlyHours((prev) => ({
      ...prev,
      [day]: prev[day] || { startHour: rate.startHour, endHour: rate.endHour },
    }));
    setTempHourlyRatesByDay((prev) => ({
      ...prev,
      [day]: {
        startHour: rate.startHour,
        endHour: rate.endHour,
        min: 1,
        max: 0,
        price: 0,
        selectedDay: day,
        type: "heure",
      },
    }));
    setHourValidationErrors((prev) => ({ ...prev, [day]: "" }));
    setNoPriceErrors((prev) => ({ ...prev, [day]: "" }));
    // Update tempSoireeRatesByDay to set startHour to the new hourly rate's endHour
    setTempSoireeRatesByDay((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        startHour: rate.endHour,
      },
    }));
  };

  const addSoireeRate = (day: string, rate: SoireeRate) => {
    // No minimum hour validation for evening rates
    setSoireeRatesByDay((prev) => ({
      ...prev,
      [day]: [...prev[day], { ...rate, selectedDay: day, type: "soirée" }],
    }));
    setFixedSoireeHours((prev) => ({
      ...prev,
      [day]: prev[day] || { startHour: rate.startHour, endHour: rate.endHour },
    }));
    setTempSoireeRatesByDay((prev) => ({
      ...prev,
      [day]: {
        startHour: rate.startHour,
        endHour: rate.endHour,
        min: 1,
        max: 0,
        price: 0,
        selectedDay: day,
        type: "soirée",
      },
    }));
    setSoireeValidationErrors((prev) => ({ ...prev, [day]: "" }));
    setNoPriceErrors((prev) => ({ ...prev, [day]: "" }));
    // Update tempHourlyRatesByDay to set endHour to the new evening rate's startHour
    setTempHourlyRatesByDay((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        endHour: rate.startHour,
      },
    }));
  };

  const addHalfDayRate = (day: string, rate: HalfDayRate) => {
    const firstRate = halfDayRatesByDay[day][0];
    const validStartHours =
      halfDayRatesByDay[day].length === 0
        ? HOURS_OPTIONS.map((opt) => opt.value)
        : [firstRate?.startHour, halfDayEndHour[day]].filter(Boolean);
    if (halfDayRatesByDay[day].length > 0 && !validStartHours.includes(rate.startHour)) {
      setMaxVisitorErrors((prev) => ({
        ...prev,
        halfDay: {
          ...prev.halfDay,
          [day]: `L'heure de début doit être ${validStartHours.join(" ou ")} pour ce jour.`,
        },
      }));
      return;
    }
    if (
      halfDayRatesByDay[day].length > 0 &&
      rate.startHour === firstRate?.startHour &&
      rate.endHour !== halfDayEndHour[day]
    ) {
      setMaxVisitorErrors((prev) => ({
        ...prev,
        halfDay: {
          ...prev.halfDay,
          [day]: `L'heure de fin doit être ${halfDayEndHour[day]} pour un début à ${firstRate.startHour}.`,
        },
      }));
      return;
    }
    if (
      halfDayRatesByDay[day].length > 0 &&
      rate.startHour === halfDayEndHour[day] &&
      secondHalfDayEndHour[day] &&
      rate.endHour !== secondHalfDayEndHour[day]
    ) {
      setMaxVisitorErrors((prev) => ({
        ...prev,
        halfDay: {
          ...prev.halfDay,
          [day]: `L'heure de fin doit être ${secondHalfDayEndHour[day]} pour un début à ${halfDayEndHour[day]}.`,
        },
      }));
      return;
    }
    setHalfDayRatesByDay((prev) => ({
      ...prev,
      [day]: [...prev[day], { ...rate, selectedDay: day, type: "demi-journée" }],
    }));
    setHalfDayEndHour((prev) => ({
      ...prev,
      [day]: halfDayRatesByDay[day].length === 0 ? rate.endHour : prev[day],
    }));
    if (rate.startHour === halfDayEndHour[day] && !secondHalfDayEndHour[day]) {
      setSecondHalfDayEndHour((prev) => ({
        ...prev,
        [day]: rate.endHour,
      }));
    }
    setTempHalfDay((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        startHour: firstRate?.startHour || "08:00",
        endHour: firstRate ? halfDayEndHour[day] || "23:00" : "23:00",
        min: 1,
        max: 0,
        price: 0,
        selectedDay: day,
        type: "demi-journée",
      },
    }));
    setNoPriceErrors((prev) => ({ ...prev, [day]: "" }));
    setMaxVisitorErrors((prev) => ({
      ...prev,
      halfDay: { ...prev.halfDay, [day]: "" },
    }));
  };

  const addFullDayRate = (day: string, rate: FullDayRate) => {
    setFullDayRatesByDay((prev) => ({
      ...prev,
      [day]: [...prev[day], { ...rate, selectedDay: day, type: "journée complète" }],
    }));
    setTempFullDay((prev) => ({
      ...prev,
      [day]: {
        startHour: "08:00",
        endHour: "23:00",
        min: 1,
        max: 0,
        price: 0,
        selectedDay: day,
        type: "journée complète",
      },
    }));
    setNoPriceErrors((prev) => ({ ...prev, [day]: "" }));
  };

  const applyRatesToAllDays = () => {
    const referenceDay = DAYS_OF_WEEK.find((day) => enabledDays[day.label])?.label || "Lundi";
    const hourlyRates = hourlyRatesByDay[referenceDay].filter((rate) => {
      const hourDiff = calculateHourDifference(rate.startHour, rate.endHour);
      return !data?.nbreHour || hourDiff >= data.nbreHour;
    });
    const soireeRates = soireeRatesByDay[referenceDay].filter((rate) => {
      const hourDiff = calculateHourDifference(rate.startHour, rate.endHour);
      return !data?.nbreHour || hourDiff >= data.nbreHour;
    });
    const halfDayRates = halfDayRatesByDay[referenceDay].slice(0, 2);
    const fullDayRates = fullDayRatesByDay[referenceDay];

    const newHourlyRates = { ...hourlyRatesByDay };
    const newSoireeRates = { ...soireeRatesByDay };
    const newHalfDayRates = { ...halfDayRatesByDay };
    const newFullDayRates = { ...fullDayRatesByDay };
    const newFixedHourlyHours = { ...fixedHourlyHours };
    const newFixedSoireeHours = { ...fixedSoireeHours };
    const newHalfDayEndHour = { ...halfDayEndHour };
    const newSecondHalfDayEndHour = { ...secondHalfDayEndHour };

    DAYS_OF_WEEK.forEach((day) => {
      newHourlyRates[day.label] = hourlyRates.map((rate) => ({
        ...rate,
        selectedDay: day.label,
        type: "heure",
      }));
      newSoireeRates[day.label] = soireeRates.map((rate) => ({
        ...rate,
        selectedDay: day.label,
        type: "soirée",
      }));

      const secondEndHour =
        halfDayRates.find((r) => r.startHour === halfDayEndHour[referenceDay])?.endHour ||
        secondHalfDayEndHour[referenceDay];
      newHalfDayRates[day.label] = halfDayRates.map((rate, index) => ({
        ...rate,
        selectedDay: day.label,
        type: "demi-journée",
        startHour:
          index === 0
            ? rate.startHour
            : [halfDayRates[0]?.startHour, halfDayEndHour[referenceDay]].includes(rate.startHour)
              ? rate.startHour
              : halfDayEndHour[referenceDay] || rate.startHour,
        endHour:
          index === 0
            ? rate.endHour
            : rate.startHour === halfDayRates[0]?.startHour
              ? halfDayEndHour[referenceDay] || "23:00"
              : rate.startHour === halfDayEndHour[referenceDay] && secondEndHour
                ? secondEndHour
                : rate.endHour || "23:00",
      }));
      newFullDayRates[day.label] = fullDayRates.map((rate) => ({
        ...rate,
        selectedDay: day.label,
        type: "journée complète",
      }));
      newFixedHourlyHours[day.label] =
        hourlyRates.length > 0 ? { startHour: hourlyRates[0].startHour, endHour: hourlyRates[0].endHour } : null;
      newFixedSoireeHours[day.label] =
        soireeRates.length > 0 ? { startHour: soireeRates[0].startHour, endHour: soireeRates[0].endHour } : null;
      newHalfDayEndHour[day.label] = halfDayRates.length > 0 ? halfDayRates[0].endHour : null;
      newSecondHalfDayEndHour[day.label] = secondEndHour;
    });

    setHourlyRatesByDay(newHourlyRates);
    setSoireeRatesByDay(newSoireeRates);
    setHalfDayRatesByDay(newHalfDayRates);
    setFullDayRatesByDay(newFullDayRates);
    setFixedHourlyHours(newFixedHourlyHours);
    setFixedSoireeHours(newFixedSoireeHours);
    setHalfDayEndHour(newHalfDayEndHour);

    setEnabledDays((prev) => {
      const newEnabledDays = { ...prev };
      DAYS_OF_WEEK.forEach((day) => {
        newEnabledDays[day.label] = true;
      });
      return newEnabledDays;
    });

    setHourValidationErrors({});
    setSoireeValidationErrors({});
    setNoPriceErrors({});
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    watch,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      prices: [],
      cleaning_costs: data?.cleaning_costs ?? 0,
      guarantee: data?.guarantee ?? 0,
      reservation_type: data?.reservation_type ?? "",
      cleaning_costs_events: data?.cleaning_costs_events ?? [],
      availableDays: enabledDays,
    },
    mode: "onChange",
  });

  const cleaningCost = watch("cleaning_costs");
  const prices = watch("prices");
  const prevEnabledDaysRef = useRef<Record<string, boolean> | null>(null);

  useEffect(() => {
    let availableDays: string[] = [];

    if (data && Array.isArray(data?.availableDays) && data?.availableDays.length > 0) {
      availableDays = data!.availableDays;
    } else if (
      data?.availableDays &&
      typeof data.availableDays === "object" &&
      !Array.isArray(data.availableDays)
    ) {
      const availableDaysObj = data.availableDays as Record<string, boolean>;
      availableDays = Object.keys(availableDaysObj).filter((day) => availableDaysObj[day]);
    } else if (prevEnabledDaysRef.current) {
      availableDays = Object.keys(prevEnabledDaysRef.current).filter((day) => prevEnabledDaysRef.current![day]);
    } else {
    }

    const updatedEnabledDays: Record<string, boolean> = {};
    DAYS_OF_WEEK.forEach((day) => {
      updatedEnabledDays[day.label] = availableDays.includes(day.label);
    });

    setEnabledDays(updatedEnabledDays);
    prevEnabledDaysRef.current = updatedEnabledDays;
  }, [data]);
  useEffect(() => {
    if (data) {
      const hourlyRates: typeof hourlyRatesByDay = { ...hourlyRatesByDay };
      const soireeRates: typeof soireeRatesByDay = { ...soireeRatesByDay };
      const halfDayRates: typeof halfDayRatesByDay = { ...halfDayRatesByDay };
      const fullDayRates: typeof fullDayRatesByDay = { ...fullDayRatesByDay };
      const newFixedHourlyHours: typeof fixedHourlyHours = { ...fixedHourlyHours };
      const newFixedSoireeHours: typeof fixedSoireeHours = { ...fixedSoireeHours };
      const newHalfDayEndHour: typeof halfDayEndHour = { ...halfDayEndHour };

      const prices = Array.isArray(data.prices) ? data.prices : [];
      prices.forEach((price) => {
        const rate = {
          startHour: price.startHour || "08:00",
          endHour: price.endHour || "23:00",
          min: price.min || 1,
          max: price.max || 0,
          price: price.price || 0,
          selectedDay: price.selectedDay,
          type: price.type || "heure",
        };

        switch (rate.type) {
          case "heure":
            hourlyRates[price.selectedDay] = [...(hourlyRates[price.selectedDay] || []), rate];
            newFixedHourlyHours[price.selectedDay] = newFixedHourlyHours[price.selectedDay] || {
              startHour: rate.startHour,
              endHour: rate.endHour,
            };
            break;
          case "soirée":
            soireeRates[price.selectedDay] = [...(soireeRates[price.selectedDay] || []), rate];
            newFixedSoireeHours[price.selectedDay] = newFixedSoireeHours[price.selectedDay] || {
              startHour: rate.startHour,
              endHour: rate.endHour,
            };
            break;
          case "demi-journée":
            if (halfDayRates[price.selectedDay].length < 2) {
              halfDayRates[price.selectedDay] = [...(halfDayRates[price.selectedDay] || []), rate];
              if (halfDayRates[price.selectedDay].length === 1) {
                newHalfDayEndHour[price.selectedDay] = rate.endHour;
              }
            }
            break;
          case "journée complète":
            fullDayRates[price.selectedDay] = [...(fullDayRates[price.selectedDay] || []), rate];
            break;
          default:
            console.warn(`Unknown price type: ${price.type}, defaulting to "heure"`);
            hourlyRates[price.selectedDay] = [
              ...(hourlyRates[price.selectedDay] || []),
              { ...rate, type: "heure" },
            ];
            newFixedHourlyHours[price.selectedDay] = newFixedHourlyHours[price.selectedDay] || {
              startHour: rate.startHour,
              endHour: rate.endHour,
            };
        }
      });

      setHourlyRatesByDay(hourlyRates);
      setSoireeRatesByDay(soireeRates);
      setHalfDayRatesByDay(halfDayRates);
      setFullDayRatesByDay(fullDayRates);
      setFixedHourlyHours(newFixedHourlyHours);
      setFixedSoireeHours(newFixedSoireeHours);
      setHalfDayEndHour(newHalfDayEndHour);

      setValue(
        "prices",
        prices.map((p) => ({
          price: p.price || 0,
          min: p.min || 1,
          max: p.max || 0,
          startHour: p.startHour || "08:00",
          endHour: p.endHour || "23:00",
          selectedDay: p.selectedDay,
          type: (p.type || "heure") as "heure" | "soirée" | "demi-journée" | "journée complète",
        }))
      );
      setValue("guarantee", data.guarantee ?? 0);
      setValue("cleaning_costs", data.cleaning_costs ?? 0);
      setValue("reservation_type", data.reservation_type ?? "");
      setValue("cleaning_costs_events", data.cleaning_costs_events ?? []);
      setValue("availableDays", enabledDays || prevEnabledDaysRef.current || {});

      prevPricesRef.current = prices.map((p) => ({
        price: p.price || 0,
        min: p.min || 1,
        max: p.max || 0,
        startHour: p.startHour || "08:00",
        endHour: p.endHour || "23:00",
        selectedDay: p.selectedDay,
        type: (p.type || "heure") as "heure" | "soirée" | "demi-journée" | "journée complète",
      }));
    }
  }, [data, setValue]);

  useEffect(() => {
    const newNoPriceErrors: { [key: string]: string } = {};
    DAYS_OF_WEEK.forEach((day) => {
      if (
        enabledDays[day.label] &&
        hourlyRatesByDay[day.label].length === 0 &&
        soireeRatesByDay[day.label].length === 0 &&
        halfDayRatesByDay[day.label].length === 0 &&
        fullDayRatesByDay[day.label].length === 0
      ) {
        newNoPriceErrors[day.label] =
          "Veuillez ajouter au moins un tarif (horaire, soirée, demi-journée ou journée complète) pour ce jour.";
      }
    });
    setNoPriceErrors(newNoPriceErrors);

    const updatedPrices = getUpdatedPrices();
    setValue("prices", updatedPrices);
    trigger("prices");
  }, [hourlyRatesByDay, soireeRatesByDay, halfDayRatesByDay, fullDayRatesByDay, enabledDays, setValue, trigger]);

  useEffect(() => {
    setValue("availableDays", enabledDays);
    trigger("availableDays");
  }, [enabledDays, setValue, trigger]);

  const eventsList = data?.events;

  useEffect(() => {
    if (handleForm.type === "chekLocationFormSix") {
      if (Object.keys(noPriceErrors).length > 0) {
        return;
      }
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  useEffect(() => {
    if (Object.values(enabledDays).some((enabled) => enabled) && (!prices || prices.length === 0)) {
      setPriceErrorMsg("Veuillez définir au moins un tarif pour les jours activés.");
    } else {
      setPriceErrorMsg("");
    }
  }, [prices, enabledDays]);

  const handleReservationTypeChange = (reservationType: string) => {
    setValue("reservation_type", reservationType);
    trigger("reservation_type");
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    if (Object.keys(noPriceErrors).length > 0) {
      return;
    }
    const updatedPrices = getUpdatedPrices();
    FormData({ ...data, prices: updatedPrices });
  };

  const [maxVisitorErrors, setMaxVisitorErrors] = useState<{
    hourly: { [key: string]: string };
    soiree: { [key: string]: string };
    halfDay: { [key: string]: string };
    fullDay: { [key: string]: string };
  }>({
    hourly: {},
    soiree: {},
    halfDay: {},
    fullDay: {},
  });

  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: { halfDay: boolean; fullDay: boolean; soiree: boolean };
  }>(() => {
    const initialExpanded: any = {};
    DAYS_OF_WEEK.forEach((day) => {
      initialExpanded[day.label] = { halfDay: false, fullDay: false, soiree: false };
    });
    return initialExpanded;
  });

  return (
    <Fragment>
      <form className="flex flex-col md:flex-row" onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full md:w-3/4 md:mr-4">
          <div>
            <h2 className="text-2xl font-semibold">Prix et disponibilité de votre espace</h2>
          </div>

          <div className="my-5">
            <FormItem label="Politique d'annulation *">
              <div className="flex space-x-4">
                <div className="relative flex-1">
                  <input
                    className="hidden peer"
                    id="radio_1"
                    type="radio"
                    {...register("reservation_type")}
                    value="Flexible"
                    checked={getValues("reservation_type") === "Flexible"}
                    onChange={(e) => handleReservationTypeChange(e.target.value)}
                  />
                  <span className="box-content absolute block w-3 h-3 -translate-y-1/2 bg-white border-8 border-gray-300 rounded-full right-4 top-1/2 peer-checked:border-indigo-500"></span>
                  <label
                    className="flex flex-col p-4 border border-gray-300 rounded-lg cursor-pointer peer-checked:border-4 peer-checked:border-indigo-700"
                    htmlFor="radio_1"
                  >
                    <span className="mt-2 text-xl font-bold">Flexible</span>
                  </label>
                </div>
                <div className="relative flex-1">
                  <input
                    className="hidden peer"
                    id="radio_2"
                    type="radio"
                    {...register("reservation_type")}
                    value="Stricte"
                    checked={getValues("reservation_type") === "Stricte"}
                    onChange={(e) => handleReservationTypeChange(e.target.value)}
                  />
                  <span className="box-content absolute block w-3 h-3 -translate-y-1/2 bg-white border-8 border-gray-300 rounded-full right-4 top-1/2 peer-checked:border-indigo-500"></span>
                  <label
                    className="flex flex-col p-4 border border-gray-300 rounded-lg cursor-pointer peer-checked:border-4 peer-checked:border-indigo-700"
                    htmlFor="radio_2"
                  >
                    <span className="mt-2 text-xl font-bold">Stricte</span>
                  </label>
                </div>
              </div>
              <ErrorMessage message={errors.reservation_type?.message || ""} />
            </FormItem>

            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <label className="font-semibold">Disponibilités et tarifs</label>
                {location.pathname === "/add-location" && (
                  <div className="flex items-center">
                    <span className="mr-2 text-sm">Appliquer à tous</span>
                    <Switch
                      checked={applyToAll}
                      onChange={(e) => {
                        const shouldApply = e.target.checked;
                        setApplyToAll(shouldApply);
                        if (shouldApply) {
                          applyRatesToAllDays();
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              <span className="block mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                Définissez les jours disponibles et les tarifs (horaire, soirée, demi-journée, journée) en fonction
                du nombre de visiteurs
              </span>

              {DAYS_OF_WEEK.map((day) => (
                <div key={day.value} className="w-full p-4 mt-4 mb-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium">{day.label}</span>
                    <Switch
                      checked={enabledDays[day.label]}
                      onChange={(e) => {
                        const isEnabled = e.target.checked;
                        setEnabledDays((prev) => ({ ...prev, [day.label]: isEnabled }));
                        if (
                          isEnabled &&
                          hourlyRatesByDay[day.label].length === 0 &&
                          soireeRatesByDay[day.label].length === 0 &&
                          halfDayRatesByDay[day.label].length === 0 &&
                          fullDayRatesByDay[day.label].length === 0
                        ) {
                          setNoPriceErrors((prev) => ({
                            ...prev,
                            [day.label]:
                              "Veuillez ajouter au moins un tarif (horaire, soirée, demi-journée ou journée complète) pour ce jour.",
                          }));
                        } else {
                          setNoPriceErrors((prev) => ({ ...prev, [day.label]: "" }));
                        }
                      }}
                    />
                  </div>
                  <ErrorMessage message={noPriceErrors[day.label] || ""} />

                  {enabledDays[day.label] && (
                    <div>
                      {/* Tarifs horaires */}
                      {/* Tarifs horaires */}
                      <div className="mb-4">
                        <p className="font-semibold">Tarifs horaires (Minimum {data?.nbreHour} heures)</p>
                        <div className="flex space-x-2">
                          <div className="w-32 h-10">
                            <Select
                              value={tempHourlyRatesByDay[day.label]?.startHour || "08:00"}
                              onChange={(e) => {
                                const newStartHour = e.target.value;
                                setTempHourlyRatesByDay((prev) => {
                                  const updated = {
                                    ...prev,
                                    [day.label]: { ...prev[day.label], startHour: newStartHour },
                                  };
                                  const hourDiff = calculateHourDifference(
                                    newStartHour,
                                    updated[day.label].endHour
                                  );
                                  if (data?.nbreHour && hourDiff < data.nbreHour) {
                                    setHourValidationErrors((prev) => ({
                                      ...prev,
                                      [day.label]: `La durée des tarifs horaires doit être d'au moins ${data.nbreHour} heures.`,
                                    }));
                                  } else {
                                    setHourValidationErrors((prev) => ({ ...prev, [day.label]: "" }));
                                  }
                                  return updated;
                                });
                                // Update tempSoireeRatesByDay to set startHour to the new hourly rate's endHour
                                setTempSoireeRatesByDay((prev) => ({
                                  ...prev,
                                  [day.label]: {
                                    ...prev[day.label],
                                    startHour: tempHourlyRatesByDay[day.label]?.endHour || "18:00",
                                  },
                                }));
                              }}
                              disabled={!!fixedHourlyHours[day.label]}
                            >
                              {HOURS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.hour}
                                </option>
                              ))}
                            </Select>
                          </div>
                          <div className="flex items-center justify-center">
                            <span>-</span>
                          </div>
                          <div className="w-32 h-10">
                            <Select
                              value={
                                tempHourlyRatesByDay[day.label]?.endHour ||
                                soireeRatesByDay[day.label]?.[0]?.startHour ||
                                "23:00"
                              }
                              onChange={(e) => {
                                const newEndHour = e.target.value;
                                setTempHourlyRatesByDay((prev) => {
                                  const updated = {
                                    ...prev,
                                    [day.label]: { ...prev[day.label], endHour: newEndHour },
                                  };
                                  const hourDiff = calculateHourDifference(
                                    updated[day.label].startHour,
                                    newEndHour
                                  );
                                  if (data?.nbreHour && hourDiff < data.nbreHour) {
                                    setHourValidationErrors((prev) => ({
                                      ...prev,
                                      [day.label]: `La durée des tarifs horaires doit être d'au moins ${data.nbreHour} heures.`,
                                    }));
                                  } else {
                                    setHourValidationErrors((prev) => ({ ...prev, [day.label]: "" }));
                                  }
                                  return updated;
                                });
                                // Update tempSoireeRatesByDay startHour
                                setTempSoireeRatesByDay((prev) => ({
                                  ...prev,
                                  [day.label]: {
                                    ...prev[day.label],
                                    startHour: newEndHour,
                                  },
                                }));
                              }}
                              disabled={
                                !!fixedHourlyHours[day.label] || !!soireeRatesByDay[day.label]?.[0]?.startHour
                              }
                            >
                              {HOURS_OPTIONS.filter((option) => {
                                const [hour] = option.value.split(":").map(Number);
                                const [startHour] = (tempHourlyRatesByDay[day.label]?.startHour || "08:00")
                                  .split(":")
                                  .map(Number);
                                const hourDiff = hour - startHour + (hour < startHour ? 24 : 0);
                                return !data?.nbreHour || hourDiff >= data.nbreHour;
                              }).map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.hour}
                                </option>
                              ))}
                            </Select>
                          </div>
                          <Input
                            type="text"
                            value={tempHourlyRatesByDay[day.label]?.min || ""}
                            placeholder="Min visiteurs"
                            onChange={(e) =>
                              setTempHourlyRatesByDay((prev) => ({
                                ...prev,
                                [day.label]: { ...prev[day.label], min: parseInt(e.target.value) || 1 },
                              }))
                            }
                            className="w-32 h-10"
                          />
                          <Input
                            type="text"
                            value={tempHourlyRatesByDay[day.label]?.max || ""}
                            placeholder="Max visiteurs"
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              if (data?.visitor && value > data.visitor) {
                                setMaxVisitorErrors((prev) => ({
                                  ...prev,
                                  hourly: {
                                    ...prev.hourly,
                                    [day.label]: `Le nombre maximum de visiteurs pour les tarifs horaires ne peut pas dépasser ${data.visitor}.`,
                                  },
                                }));
                              } else {
                                setTempHourlyRatesByDay((prev) => ({
                                  ...prev,
                                  [day.label]: { ...prev[day.label], max: value },
                                }));
                                setMaxVisitorErrors((prev) => ({
                                  ...prev,
                                  hourly: { ...prev.hourly, [day.label]: "" },
                                }));
                              }
                            }}
                            className={`w-32 h-10 ${maxVisitorErrors.hourly[day.label] ? "border-red-500" : ""}`}
                          />
                          <Input
                            type="text"
                            value={tempHourlyRatesByDay[day.label]?.price || ""}
                            placeholder="Prix €"
                            onChange={(e) =>
                              setTempHourlyRatesByDay((prev) => ({
                                ...prev,
                                [day.label]: { ...prev[day.label], price: parseFloat(e.target.value) || 0 },
                              }))
                            }
                            className="w-24 h-10"
                          />
                          <ButtonPrimary
                            type="button"
                            onClick={() => {
                              const rate = {
                                ...tempHourlyRatesByDay[day.label],
                                startHour:
                                  fixedHourlyHours[day.label]?.startHour ||
                                  tempHourlyRatesByDay[day.label].startHour,
                                endHour:
                                  fixedHourlyHours[day.label]?.endHour ||
                                  soireeRatesByDay[day.label]?.[0]?.startHour ||
                                  tempHourlyRatesByDay[day.label].endHour,
                              };
                              const hourDiff = calculateHourDifference(rate.startHour, rate.endHour);
                              if (
                                rate &&
                                rate.startHour &&
                                rate.endHour &&
                                rate.min &&
                                rate.max &&
                                rate.price &&
                                !maxVisitorErrors.hourly[day.label] &&
                                (!data?.nbreHour || hourDiff >= data.nbreHour)
                              ) {
                                addHourlyRate(day.label, rate);
                              } else if (data?.nbreHour && hourDiff < data.nbreHour) {
                                setHourValidationErrors((prev) => ({
                                  ...prev,
                                  [day.label]: `La durée des tarifs horaires doit être d'au moins ${data.nbreHour} heures.`,
                                }));
                              }
                            }}
                            className="flex items-center justify-center w-24 h-10 text-white bg-blue-500 rounded hover:bg-blue-600"
                            disabled={!!maxVisitorErrors.hourly[day.label] || !!hourValidationErrors[day.label]}
                          >
                            +
                          </ButtonPrimary>
                        </div>
                        <ErrorMessage
                          message={hourValidationErrors[day.label] || maxVisitorErrors.hourly[day.label] || ""}
                          mode="horaire"
                        />
                        {hourlyRatesByDay[day.label]?.map((rate, index) => (
                          <div key={index} className="flex items-center mt-2 space-x-2">
                            <Input type="text" value={rate.startHour} className="w-32 h-10" disabled />
                            <div className="flex items-center justify-center">
                              <span>-</span>
                            </div>
                            <Input type="text" value={rate.endHour} className="w-32 h-10" disabled />
                            <Input type="number" value={rate.min} className="w-32 h-10" disabled />
                            <Input type="number" value={rate.max} className="w-32 h-10" disabled />
                            <Input type="number" value={rate.price} className="w-24 h-10" disabled />
                            <ButtonPrimary
                              type="button"
                              onClick={() => {
                                setHourlyRatesByDay((prev) => {
                                  const newRates = {
                                    ...prev,
                                    [day.label]: prev[day.label].filter((_, i) => i !== index),
                                  };
                                  return newRates;
                                });
                                setFixedHourlyHours((prev) => ({
                                  ...prev,
                                  [day.label]: hourlyRatesByDay[day.label].length - 1 > 0 ? prev[day.label] : null,
                                }));
                                setTempSoireeRatesByDay((prev) => ({
                                  ...prev,
                                  [day.label]: {
                                    ...prev[day.label],
                                    startHour: soireeRatesByDay[day.label]?.[0]?.startHour || "18:00",
                                  },
                                }));
                                setTimeout(() => {
                                  if (
                                    enabledDays[day.label] &&
                                    hourlyRatesByDay[day.label].length - 1 === 0 &&
                                    soireeRatesByDay[day.label].length === 0 &&
                                    halfDayRatesByDay[day.label].length === 0 &&
                                    fullDayRatesByDay[day.label].length === 0
                                  ) {
                                    setNoPriceErrors((prev) => ({
                                      ...prev,
                                      [day.label]:
                                        "Veuillez ajouter au moins un tarif (horaire, soirée, demi-journée ou journée complète) pour ce jour.",
                                    }));
                                  }
                                }, 0);
                              }}
                              className="flex items-center justify-center w-24 text-white bg-red-500 hover:bg-red-600"
                            >
                              X
                            </ButtonPrimary>
                          </div>
                        ))}
                      </div>

                      {/* Tarifs soirée */}
                      <div className="mb-4">
                        <p
                          className="font-semibold cursor-pointer"
                          onClick={() =>
                            setExpandedSections((prev) => ({
                              ...prev,
                              [day.label]: {
                                ...prev[day.label],
                                soiree: !prev[day.label].soiree,
                              },
                            }))
                          }
                        >
                          Tarifs soirée {expandedSections[day.label].soiree ? "▼" : "▶"}
                        </p>
                        {expandedSections[day.label].soiree && (
                          <>
                            <div className="flex space-x-2">
                              <div className="w-32 h-10">
                                <Select
                                  value={
                                    tempSoireeRatesByDay[day.label]?.startHour ||
                                    hourlyRatesByDay[day.label]?.[0]?.endHour ||
                                    "18:00"
                                  }
                                  onChange={(e) => {
                                    const newStartHour = e.target.value;
                                    setTempSoireeRatesByDay((prev) => ({
                                      ...prev,
                                      [day.label]: { ...prev[day.label], startHour: newStartHour },
                                    }));
                                    // Update tempHourlyRatesByDay endHour
                                    setTempHourlyRatesByDay((prev) => ({
                                      ...prev,
                                      [day.label]: {
                                        ...prev[day.label],
                                        endHour: newStartHour,
                                      },
                                    }));
                                  }}
                                  disabled={
                                    !!fixedSoireeHours[day.label] || !!hourlyRatesByDay[day.label]?.[0]?.endHour
                                  }
                                >
                                  {(hourlyRatesByDay[day.label]?.[0]?.endHour
                                    ? [
                                        {
                                          hour: hourlyRatesByDay[day.label][0].endHour,
                                          value: hourlyRatesByDay[day.label][0].endHour,
                                        },
                                      ]
                                    : HOURS_OPTIONS
                                  ).map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.hour}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                              <div className="flex items-center justify-center">
                                <span>-</span>
                              </div>
                              <div className="w-32 h-10">
                                <Select
                                  value={tempSoireeRatesByDay[day.label]?.endHour || "23:00"}
                                  onChange={(e) => {
                                    setTempSoireeRatesByDay((prev) => ({
                                      ...prev,
                                      [day.label]: { ...prev[day.label], endHour: e.target.value },
                                    }));
                                  }}
                                >
                                  {HOURS_OPTIONS.filter((option) => {
                                    const [hour] = option.value.split(":").map(Number);
                                    const [startHour] = (
                                      tempSoireeRatesByDay[day.label]?.startHour ||
                                      hourlyRatesByDay[day.label]?.[0]?.endHour ||
                                      "18:00"
                                    )
                                      .split(":")
                                      .map(Number);
                                    return hour > startHour;
                                  }).map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.hour}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                              <Input
                                type="text"
                                value={tempSoireeRatesByDay[day.label]?.min || ""}
                                placeholder="Min visiteurs"
                                onChange={(e) =>
                                  setTempSoireeRatesByDay((prev) => ({
                                    ...prev,
                                    [day.label]: { ...prev[day.label], min: parseInt(e.target.value) || 1 },
                                  }))
                                }
                                className="w-32 h-10"
                              />
                              <Input
                                type="text"
                                value={tempSoireeRatesByDay[day.label]?.max || ""}
                                placeholder="Max visiteurs"
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  if (data?.visitor && value > data.visitor) {
                                    setMaxVisitorErrors((prev) => ({
                                      ...prev,
                                      soiree: {
                                        ...prev.soiree,
                                        [day.label]: `Le nombre maximum de visiteurs pour les tarifs soirée ne peut pas dépasser ${data.visitor}.`,
                                      },
                                    }));
                                  } else {
                                    setTempSoireeRatesByDay((prev) => ({
                                      ...prev,
                                      [day.label]: { ...prev[day.label], max: value },
                                    }));
                                    setMaxVisitorErrors((prev) => ({
                                      ...prev,
                                      soiree: { ...prev.soiree, [day.label]: "" },
                                    }));
                                  }
                                }}
                                className={`w-32 h-10 ${maxVisitorErrors.soiree[day.label] ? "border-red-500" : ""}`}
                              />
                              <Input
                                type="text"
                                value={tempSoireeRatesByDay[day.label]?.price || ""}
                                placeholder="Prix €"
                                onChange={(e) =>
                                  setTempSoireeRatesByDay((prev) => ({
                                    ...prev,
                                    [day.label]: { ...prev[day.label], price: parseFloat(e.target.value) || 0 },
                                  }))
                                }
                                className="w-24 h-10"
                              />
                              <ButtonPrimary
                                type="button"
                                onClick={() => {
                                  const rate = {
                                    ...tempSoireeRatesByDay[day.label],
                                    startHour:
                                      fixedSoireeHours[day.label]?.startHour ||
                                      hourlyRatesByDay[day.label]?.[0]?.endHour ||
                                      tempSoireeRatesByDay[day.label].startHour,
                                    endHour:
                                      fixedSoireeHours[day.label]?.endHour ||
                                      tempSoireeRatesByDay[day.label].endHour,
                                  };
                                  if (
                                    rate &&
                                    rate.startHour &&
                                    rate.endHour &&
                                    rate.min &&
                                    rate.max &&
                                    rate.price &&
                                    !maxVisitorErrors.soiree[day.label]
                                  ) {
                                    addSoireeRate(day.label, rate);
                                  }
                                }}
                                className="flex items-center justify-center w-24 h-10 text-white bg-blue-500 rounded hover:bg-blue-600"
                                disabled={!!maxVisitorErrors.soiree[day.label]}
                              >
                                +
                              </ButtonPrimary>
                            </div>

                            <ErrorMessage message={maxVisitorErrors.soiree[day.label] || ""} mode="soirée" />
                            {soireeRatesByDay[day.label]?.map((rate, index) => (
                              <div key={index} className="flex items-center mt-2 space-x-2">
                                <Input type="text" value={rate.startHour} className="w-32 h-10" disabled />
                                <div className="flex items-center justify-center">
                                  <span>-</span>
                                </div>
                                <Input type="text" value={rate.endHour} className="w-32 h-10" disabled />
                                <Input type="number" value={rate.min} className="w-32 h-10" disabled />
                                <Input type="number" value={rate.max} className="w-32 h-10" disabled />
                                <Input type="number" value={rate.price} className="w-24 h-10" disabled />
                                <ButtonPrimary
                                  type="button"
                                  onClick={() => {
                                    setSoireeRatesByDay((prev) => {
                                      const newRates = {
                                        ...prev,
                                        [day.label]: prev[day.label].filter((_, i) => i !== index),
                                      };
                                      return newRates;
                                    });
                                    setFixedSoireeHours((prev) => ({
                                      ...prev,
                                      [day.label]:
                                        soireeRatesByDay[day.label].length - 1 > 0 ? prev[day.label] : null,
                                    }));
                                    setTempHourlyRatesByDay((prev) => ({
                                      ...prev,
                                      [day.label]: {
                                        ...prev[day.label],
                                        endHour: hourlyRatesByDay[day.label]?.[0]?.endHour || "23:00",
                                      },
                                    }));
                                    setTimeout(() => {
                                      if (
                                        enabledDays[day.label] &&
                                        hourlyRatesByDay[day.label].length === 0 &&
                                        soireeRatesByDay[day.label].length - 1 === 0 &&
                                        halfDayRatesByDay[day.label].length === 0 &&
                                        fullDayRatesByDay[day.label].length === 0
                                      ) {
                                        setNoPriceErrors((prev) => ({
                                          ...prev,
                                          [day.label]:
                                            "Veuillez ajouter au moins un tarif (horaire, soirée, demi-journée ou journée complète) pour ce jour.",
                                        }));
                                      }
                                    }, 0);
                                  }}
                                  className="flex items-center justify-center w-24 text-white bg-red-500 hover:bg-red-600"
                                >
                                  X
                                </ButtonPrimary>
                              </div>
                            ))}
                          </>
                        )}
                      </div>

                      {/* Tarifs demi-journée */}

                      <div className="mb-4">
                        <p
                          className="font-semibold cursor-pointer"
                          onClick={() =>
                            setExpandedSections((prev) => ({
                              ...prev,
                              [day.label]: {
                                ...prev[day.label],
                                halfDay: !prev[day.label].halfDay,
                              },
                            }))
                          }
                        >
                          Tarifs demi-journée {expandedSections[day.label].halfDay ? "▼" : "▶"}
                        </p>
                        {expandedSections[day.label].halfDay && (
                          <>
                            <div className="flex mt-2 space-x-2">
                              <div className="w-32 h-10">
                                <Select
                                  value={tempHalfDay[day.label]?.startHour || "08:00"}
                                  onChange={(e) => {
                                    const newStartHour = e.target.value;
                                    const firstRate = halfDayRatesByDay[day.label][0];
                                    setTempHalfDay((prev: any) => ({
                                      ...prev,
                                      [day.label]: {
                                        ...prev[day.label],
                                        startHour: newStartHour,
                                        endHour:
                                          firstRate && newStartHour === firstRate.startHour
                                            ? halfDayEndHour[day.label] || "23:00"
                                            : newStartHour === halfDayEndHour[day.label] &&
                                                secondHalfDayEndHour[day.label]
                                              ? secondHalfDayEndHour[day.label]
                                              : "23:00",
                                      },
                                    }));
                                    const validStartHours =
                                      halfDayRatesByDay[day.label].length === 0
                                        ? HOURS_OPTIONS.map((opt) => opt.value)
                                        : [firstRate?.startHour, halfDayEndHour[day.label]].filter(Boolean);
                                    if (
                                      halfDayRatesByDay[day.label].length > 0 &&
                                      !validStartHours.includes(newStartHour)
                                    ) {
                                      setMaxVisitorErrors((prev) => ({
                                        ...prev,
                                        halfDay: {
                                          ...prev.halfDay,
                                          [day.label]: `L'heure de début doit être ${validStartHours.join(" ou ")} pour ce jour.`,
                                        },
                                      }));
                                    } else {
                                      setMaxVisitorErrors((prev) => ({
                                        ...prev,
                                        halfDay: { ...prev.halfDay, [day.label]: "" },
                                      }));
                                    }
                                  }}
                                >
                                  {(halfDayRatesByDay[day.label].length === 0
                                    ? HOURS_OPTIONS
                                    : [
                                        {
                                          hour: halfDayRatesByDay[day.label][0].startHour,
                                          value: halfDayRatesByDay[day.label][0].startHour,
                                        },
                                        {
                                          hour: halfDayEndHour[day.label] || "15:00",
                                          value: halfDayEndHour[day.label] || "15:00",
                                        },
                                      ]
                                  ).map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.value}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                              <div className="flex items-center justify-center">
                                <span>-</span>
                              </div>
                              <div className="w-32 h-10">
                                <Select
                                  value={tempHalfDay[day.label]?.endHour || "23:00"}
                                  onChange={(e) =>
                                    setTempHalfDay((prev) => ({
                                      ...prev,
                                      [day.label]: { ...prev[day.label], endHour: e.target.value },
                                    }))
                                  }
                                  disabled={
                                    halfDayRatesByDay[day.label].length > 0 &&
                                    (tempHalfDay[day.label]?.startHour ===
                                      halfDayRatesByDay[day.label][0]?.startHour ||
                                      (tempHalfDay[day.label]?.startHour === halfDayEndHour[day.label] &&
                                        !!secondHalfDayEndHour[day.label]))
                                  }
                                >
                                  {(halfDayRatesByDay[day.label].length > 0 &&
                                  tempHalfDay[day.label]?.startHour === halfDayEndHour[day.label] &&
                                  !secondHalfDayEndHour[day.label]
                                    ? HOURS_OPTIONS.filter((opt) => {
                                        const [hour] = opt.value.split(":").map(Number);
                                        return hour >= 15;
                                      })
                                    : HOURS_OPTIONS
                                  ).map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.value}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                              <Input
                                type="text"
                                value={tempHalfDay[day.label]?.min || ""}
                                placeholder="Min visiteurs"
                                onChange={(e) =>
                                  setTempHalfDay((prev) => ({
                                    ...prev,
                                    [day.label]: { ...prev[day.label], min: parseInt(e.target.value) || 1 },
                                  }))
                                }
                                className="w-32 h-10"
                              />
                              <Input
                                type="text"
                                value={tempHalfDay[day.label]?.max || ""}
                                placeholder="Max visiteurs"
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  if (data?.visitor && value > data.visitor) {
                                    setMaxVisitorErrors((prev) => ({
                                      ...prev,
                                      halfDay: {
                                        ...prev.halfDay,
                                        [day.label]: `Le nombre maximum de visiteurs pour les tarifs demi-journée ne peut pas dépasser ${data.visitor}.`,
                                      },
                                    }));
                                  } else {
                                    setTempHalfDay((prev) => ({
                                      ...prev,
                                      [day.label]: { ...prev[day.label], max: value },
                                    }));
                                    setMaxVisitorErrors((prev) => ({
                                      ...prev,
                                      halfDay: { ...prev.halfDay, [day.label]: "" },
                                    }));
                                  }
                                }}
                                className={`w-32 h-10 ${maxVisitorErrors.halfDay[day.label] ? "border-red-500" : ""}`}
                              />
                              <Input
                                type="text"
                                value={tempHalfDay[day.label]?.price || ""}
                                placeholder="Prix €"
                                onChange={(e) =>
                                  setTempHalfDay((prev) => ({
                                    ...prev,
                                    [day.label]: { ...prev[day.label], price: parseFloat(e.target.value) || 0 },
                                  }))
                                }
                                className="w-24 h-10"
                              />
                              <ButtonPrimary
                                type="button"
                                onClick={() => {
                                  const firstRate = halfDayRatesByDay[day.label][0];
                                  const rate = {
                                    ...tempHalfDay[day.label],
                                    startHour: tempHalfDay[day.label].startHour,
                                    endHour:
                                      halfDayRatesByDay[day.label].length > 0 &&
                                      tempHalfDay[day.label].startHour === firstRate?.startHour
                                        ? halfDayEndHour[day.label] || "23:00"
                                        : tempHalfDay[day.label].startHour === halfDayEndHour[day.label] &&
                                            secondHalfDayEndHour[day.label]
                                          ? secondHalfDayEndHour[day.label]
                                          : tempHalfDay[day.label].endHour,
                                  };
                                  if (
                                    rate &&
                                    rate.min &&
                                    rate.max &&
                                    rate.price &&
                                    !maxVisitorErrors.halfDay[day.label]
                                  ) {
                                    addHalfDayRate(
                                      day.label,
                                      rate as {
                                        startHour: string;
                                        endHour: string;
                                        min: number;
                                        max: number;
                                        price: number;
                                        type: "demi-journée";
                                      }
                                    );
                                  }
                                }}
                                className="flex items-center justify-center w-24 h-10 text-white bg-blue-500 rounded hover:bg-blue-600"
                                disabled={!!maxVisitorErrors.halfDay[day.label]}
                              >
                                +
                              </ButtonPrimary>
                            </div>
                            <ErrorMessage
                              message={maxVisitorErrors.halfDay[day.label] || ""}
                              mode="demi-journée"
                            />
                            {halfDayRatesByDay[day.label]?.map((rate, index) => (
                              <div key={index} className="flex items-center mt-2 space-x-2">
                                <Input type="text" value={rate.startHour} className="w-32 h-10" disabled />
                                <div className="flex items-center justify-center">
                                  <span>-</span>
                                </div>
                                <Input type="text" value={rate.endHour} className="w-32 h-10" disabled />
                                <Input type="number" value={rate.min} className="w-32 h-10" disabled />
                                <Input type="number" value={rate.max} className="w-32 h-10" disabled />
                                <Input type="number" value={rate.price} className="w-24 h-10" disabled />
                                <ButtonPrimary
                                  type="button"
                                  onClick={() => {
                                    setHalfDayRatesByDay((prev) => ({
                                      ...prev,
                                      [day.label]: prev[day.label].filter((_, i) => i !== index),
                                    }));
                                    setHalfDayEndHour((prev) => ({
                                      ...prev,
                                      [day.label]:
                                        halfDayRatesByDay[day.label].length - 1 > 0 ? prev[day.label] : null,
                                    }));
                                    setSecondHalfDayEndHour((prev) => ({
                                      ...prev,
                                      [day.label]:
                                        halfDayRatesByDay[day.label].length - 1 > 0 &&
                                        halfDayRatesByDay[day.label].some(
                                          (r, i) => i !== index && r.startHour === halfDayEndHour[day.label]
                                        )
                                          ? prev[day.label]
                                          : null,
                                    }));
                                    setTempHalfDay((prev) => ({
                                      ...prev,
                                      [day.label]: {
                                        ...prev[day.label],
                                        startHour:
                                          halfDayRatesByDay[day.label].length - 1 > 0
                                            ? halfDayRatesByDay[day.label][0]?.startHour || "08:00"
                                            : "08:00",
                                        endHour:
                                          halfDayRatesByDay[day.label].length - 1 > 0
                                            ? halfDayEndHour[day.label] || "23:00"
                                            : "23:00",
                                      },
                                    }));
                                    setTimeout(() => {
                                      if (
                                        enabledDays[day.label] &&
                                        hourlyRatesByDay[day.label].length === 0 &&
                                        soireeRatesByDay[day.label].length === 0 &&
                                        halfDayRatesByDay[day.label].length - 1 === 0 &&
                                        fullDayRatesByDay[day.label].length === 0
                                      ) {
                                        setNoPriceErrors((prev) => ({
                                          ...prev,
                                          [day.label]:
                                            "Veuillez ajouter au moins un tarif (horaire, soirée, demi-journée ou journée complète) pour ce jour.",
                                        }));
                                      }
                                    }, 10);
                                  }}
                                  className="flex items-center justify-center w-24 h-10 text-white bg-red-500 rounded hover:bg-red-600"
                                >
                                  X
                                </ButtonPrimary>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                      {/* Tarifs journée */}
                      <div className="mb-4">
                        <p
                          className="font-semibold cursor-pointer"
                          onClick={() =>
                            setExpandedSections((prev) => ({
                              ...prev,
                              [day.label]: {
                                ...prev[day.label],
                                fullDay: !prev[day.label].fullDay,
                              },
                            }))
                          }
                        >
                          Tarifs journée {expandedSections[day.label].fullDay ? "▼" : "▶"}
                        </p>
                        {expandedSections[day.label].fullDay && (
                          <>
                            <div className="flex mt-2 space-x-2">
                              <div className="w-32 h-10">
                                <Select
                                  value={tempFullDay[day.label]?.startHour || "08:00"}
                                  onChange={(e) =>
                                    setTempFullDay((prev) => ({
                                      ...prev,
                                      [day.label]: { ...prev[day.label], startHour: e.target.value },
                                    }))
                                  }
                                >
                                  {HOURS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.hour}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                              <div className="flex items-center justify-center">
                                <span>-</span>
                              </div>
                              <div className="w-32 h-10">
                                <Select
                                  value={tempFullDay[day.label]?.endHour || "20:00"}
                                  onChange={(e) =>
                                    setTempFullDay((prev) => ({
                                      ...prev,
                                      [day.label]: { ...prev[day.label], endHour: e.target.value },
                                    }))
                                  }
                                >
                                  {HOURS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.hour}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                              <Input
                                type="text"
                                value={tempFullDay[day.label]?.min || ""}
                                placeholder="Min visiteurs"
                                onChange={(e) =>
                                  setTempFullDay((prev) => ({
                                    ...prev,
                                    [day.label]: { ...prev[day.label], min: parseInt(e.target.value) || 1 },
                                  }))
                                }
                                className="w-32 h-10"
                              />
                              <Input
                                type="text"
                                value={tempFullDay[day.label]?.max || ""}
                                placeholder="Max visiteurs"
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  if (data?.visitor && value > data.visitor) {
                                    setMaxVisitorErrors((prev) => ({
                                      ...prev,
                                      fullDay: {
                                        ...prev.fullDay,
                                        [day.label]: `Le nombre maximum de visiteurs pour les tarifs journée complète ne peut pas dépasser ${data.visitor}.`,
                                      },
                                    }));
                                  } else {
                                    setTempFullDay((prev) => ({
                                      ...prev,
                                      [day.label]: { ...prev[day.label], max: value },
                                    }));
                                    setMaxVisitorErrors((prev) => ({
                                      ...prev,
                                      fullDay: { ...prev.fullDay, [day.label]: "" },
                                    }));
                                  }
                                }}
                                className={`w-32 h-10 ${maxVisitorErrors.fullDay[day.label] ? "border-red-500" : ""}`}
                              />
                              <Input
                                type="text"
                                value={tempFullDay[day.label]?.price || ""}
                                placeholder="Prix €"
                                onChange={(e) =>
                                  setTempFullDay((prev) => ({
                                    ...prev,
                                    [day.label]: { ...prev[day.label], price: parseFloat(e.target.value) || 0 },
                                  }))
                                }
                                className="w-24 h-10"
                              />
                              <ButtonPrimary
                                type="button"
                                onClick={() => {
                                  const rate = tempFullDay[day.label];
                                  if (
                                    rate &&
                                    rate.min &&
                                    rate.max &&
                                    rate.price &&
                                    !maxVisitorErrors.fullDay[day.label]
                                  ) {
                                    addFullDayRate(day.label, rate);
                                  }
                                }}
                                className="flex items-center justify-center w-24 h-10 text-white bg-blue-500 rounded hover:bg-blue-600"
                                disabled={!!maxVisitorErrors.fullDay[day.label]}
                              >
                                +
                              </ButtonPrimary>
                            </div>
                            <ErrorMessage
                              message={maxVisitorErrors.fullDay[day.label] || ""}
                              mode="journée complète"
                            />
                            {fullDayRatesByDay[day.label]?.map((rate, index) => (
                              <div key={index} className="flex items-center mt-2 space-x-2">
                                <Input type="text" value={rate.startHour} className="w-32 h-10" disabled />
                                <div className="flex items-center justify-center">
                                  <span>-</span>
                                </div>
                                <Input type="text" value={rate.endHour} className="w-32 h-10" disabled />
                                <Input type="number" value={rate.min} className="w-32 h-10" disabled />
                                <Input type="number" value={rate.max} className="w-32 h-10" disabled />
                                <Input type="number" value={rate.price} className="w-24 h-10" disabled />
                                <ButtonPrimary
                                  type="button"
                                  onClick={() => {
                                    setFullDayRatesByDay((prev) => ({
                                      ...prev,
                                      [day.label]: prev[day.label].filter((_, i) => i !== index),
                                    }));
                                    setTimeout(() => {
                                      if (
                                        enabledDays[day.label] &&
                                        hourlyRatesByDay[day.label].length === 0 &&
                                        soireeRatesByDay[day.label].length === 0 &&
                                        halfDayRatesByDay[day.label].length === 0 &&
                                        fullDayRatesByDay[day.label].length - 1 === 0
                                      ) {
                                        setNoPriceErrors((prev) => ({
                                          ...prev,
                                          [day.label]:
                                            "Veuillez ajouter au moins un tarif (horaire, soirée, demi-journée ou journée complète) pour ce jour.",
                                        }));
                                      }
                                    }, 0);
                                  }}
                                  className="flex items-center justify-center w-24 text-white bg-red-500 hover:bg-red-600"
                                >
                                  X
                                </ButtonPrimary>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <ErrorMessage message={errors.prices?.message || ""} />
            </div>
            <ErrorMessage message={priceErrorMsg} />

            <div className="flex justify-between w-full space-x-5">
              <FormItem label="Frais de ménage (€)" className="mt-3">
                <div className="relative">
                  <div className="absolute inset-y-0 z-10 flex items-center pl-1 pointer-events-none right-5">
                    <span className="text-gray-500">€</span>
                  </div>
                  <Input
                    className={`mt-1.5 ${errors.cleaning_costs?.message ? "border-red-500" : ""}`}
                    placeholder="Frais de ménage"
                    {...register("cleaning_costs", { setValueAs: (v) => (v === "" ? undefined : parseInt(v)) })}
                    type="text"
                  />
                </div>
                <ErrorMessage message={errors.cleaning_costs?.message || ""} />
              </FormItem>

              {getValues("cleaning_costs") !== 0 && getValues("cleaning_costs") !== undefined && (
                <FormItem label="Sélectionner liste des événements" className="mt-3">
                  <Multiselect
                    options={eventsList?.map((item: any) => ({ label: item, value: item }))}
                    selectedValues={getValues("cleaning_costs_events")?.map((label: string) => ({
                      label,
                      value: label,
                    }))}
                    onSelect={(selectedList) => {
                      const labels = selectedList.map((item: { label: any }) => item.label);
                      setValue("cleaning_costs_events", labels);
                    }}
                    onRemove={(selectedList) => {
                      const labels = selectedList.map((item: { label: any }) => item.label);
                      setValue("cleaning_costs_events", labels);
                    }}
                    displayValue="label"
                    className={errors.cleaning_costs_events?.message ? "border-red-500" : ""}
                    placeholder="Sélectionner événements"
                    closeOnSelect={false}
                    disable={isLoading}
                    showCheckbox
                    isObject={true}
                    style={{
                      chips: { borderRadius: "20px" },
                      multiselectContainer: { borderRadius: "10px" },
                      searchBox: { borderRadius: "10px" },
                      optionContainer: { borderRadius: "10px" },
                    }}
                  />
                  <ErrorMessage message={errors.cleaning_costs_events?.message || ""} />
                </FormItem>
              )}
            </div>

            <FormItem label="Montant du dépôt de garantie (€) *" className="mt-3">
              <div className="md:w-1/2">
                <div className="relative">
                  <div className="absolute inset-y-0 z-10 flex items-center pl-1 pointer-events-none right-5">
                    <span className="text-gray-500">€</span>
                  </div>
                  <Input
                    className={`mt-1.5 ${errors.guarantee?.message ? "border-red-500" : ""}`}
                    placeholder="Montant du dépôt de garantie"
                    {...register("guarantee", { valueAsNumber: true })}
                    type="text"
                  />
                </div>
              </div>
              <ErrorMessage message={errors.guarantee?.message || ""} />
            </FormItem>
          </div>
        </div>

        <div className="w-full mt-4 md:w-1/3 md:mt-0">
          <div className="p-4 border-2 border-indigo-800">
            <div className="flex items-center mt-5 ml-5">
              <CurrencyEuroIcon className="inline-block w-8" />
              <h2 className="inline-block mt-0 ml-2 text-2xl font-semibold">
                Politique d'annulation <span className="text-xs">(Flexible/Stricte)</span>
              </h2>
            </div>
            <div className="mt-2 ml-16">
              <ul className="mr-5 -ml-3 text-sm list-disc">
                <li>Stricte : Le client ne peut pas annuler la réservation après 48 heures de sa création</li>
                <li>
                  Flexible : Le client ne peut pas annuler la réservation moins de 5 jours avant la date réservée.
                </li>
              </ul>
            </div>

            <div className="flex items-center mt-5 ml-5">
              <CurrencyEuroIcon className="inline-block w-8" />
              <h2 className="inline-block mt-0 ml-2 text-2xl font-semibold">
                Montant de la location <span className="text-xs">(prix/heure, soirée, demi-journée</span>
              </h2>
            </div>
            <div className="mt-2 ml-16">
              <p className="mr-5 -ml-10 text-sm">
                Indiquez les tarifs de location pour l'heure, la demi-journée ou la journée complète.
              </p>
            </div>

            <div className="flex items-center mt-10 ml-5">
              <CurrencyEuroIcon className="inline-block w-8" />
              <h2 className="inline-block mt-0 ml-2 text-2xl font-semibold">Montant du dépôt de garantie</h2>
            </div>
            <div className="mt-2 ml-16">
              <p className="mt-2 -ml-10 text-sm">
                Précisez le montant du dépôt de garantie requis lors de la location de l'espace.
              </p>
            </div>

            <div className="flex items-center mt-10 ml-5">
              <CalendarDaysIcon className="inline-block w-8" />
              <h2 className="inline-block mt-0 ml-2 text-2xl font-semibold">Disponibilité</h2>
            </div>
            <div className="mt-2 ml-16">
              <p className="mb-5 -ml-10 text-sm">
                Définissez les jours où votre espace est disponible à la location.
              </p>
            </div>

            <div className="flex items-center mt-10 ml-5">
              <ClockIcon className="inline-block w-8" />
              <h2 className="inline-block mt-0 ml-2 text-2xl font-semibold">
                Horaire <span className="text-xs">(Heure de début)</span>
              </h2>
            </div>
            <div className="mt-2 ml-16">
              <p className="mb-5 -ml-10 text-sm">
                Indiquez l'heure à laquelle la location de l'espace peut commencer.
              </p>
            </div>

            <div className="flex items-center mt-10 ml-5">
              <ClockIcon className="inline-block w-8" />
              <h2 className="inline-block mt-0 ml-2 text-2xl font-semibold">
                Horaire <span className="text-xs">(Heure de fin)</span>
              </h2>
            </div>
            <div className="mt-2 ml-16">
              <p className="mb-5 -ml-10 text-sm">
                Précisez l'heure à laquelle la location de l'espace doit être terminée.
              </p>
            </div>
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationAvailibility;
